import { JsonObject, JsonProperty } from 'json2typescript';
import { Service } from '../services/service';
import { BankDetail } from './bankDetail';
import { ClientContract } from './clientContract';
import { Contact } from './contact';
import { Note } from './note';
import { Partner } from './partner';
import { Document } from '../documents/document';
import { Lawyer } from '../lawyers/lawyer';

export enum StatusEnum {
    Active = 1,
    Inactive = 2,
    Disconnected = 3,
}


@JsonObject('ClientBase')
export class ClientBase {
    @JsonProperty('id', Number, true)
    id: number = undefined;

    @JsonProperty('name', String, true)
    name: string = null;

    @JsonProperty('name_ar', String, true)
    nameAr: string = null;

    @JsonProperty('city', String, true)
    city: string = null;

    @JsonProperty('country', String, true)
    country: string = null;

    @JsonProperty('status', Number, true)
    status: StatusEnum = null;

    @JsonProperty('created_date', String, true)
    createdDate = null;

    @JsonProperty('updated_date', String, true)
    updatedDate = null;


    @JsonProperty('open_cases_count', Number, true)
    openCasesCount = 0;

    @JsonProperty('closed_cases_count', Number, true)
    closedCasesCount = 0;

    @JsonProperty('created_by', String, true)
    createdBy: string = null;

    @JsonProperty('has_conflict', Boolean, true)
    hasConflict = false;

    get fullName() {
        return this.nameAr || this.name;
    }

    get location() {
        if (this.city && this.country) {
            return this.city + ', ' + this.country;
        } else if (this.city && !this.country) {
            return this.city;
        } else {
            return this.country;
        }
    }

    get totalCases() {
        return this.openCasesCount + this.closedCasesCount;
    }
}

@JsonObject('Client')
export class Client extends ClientBase {
    @JsonProperty('id', Number, true)
    id: number = undefined;

    @JsonProperty('created_by', String, true)
    createdBy: string = null;

    @JsonProperty('street_address', String, true)
    streetAddress = '';

    @JsonProperty('landline', String, true)
    landline = '';

    @JsonProperty('phone', String, true)
    phone = '';

    @JsonProperty('email', String, true)
    email = '';

    @JsonProperty('vat_num', String, true)
    vatNum = '';

    @JsonProperty('ird_num', String, true)
    irdNum = '';

    @JsonProperty('gst_num', String, true)
    gstNum = '';

    @JsonProperty('taxation_num', String, true)
    taxationNumber = '';

    @JsonProperty('company_status', String, true)
    companyStatus = '';

    @JsonProperty('company_activity', String, true)
    companyActivity = '';

    @JsonProperty('company_type', String, true)
    companyType = '';

    @JsonProperty('crm_lawyer', Lawyer, true)
    crmLawyer: Lawyer = null;

    @JsonProperty('crm_lawyer_id', Number, true)
    crmLawyerId: number = null;

    @JsonProperty('governmental_approvals', String, true)
    governmentalApprovals = '';

    @JsonProperty('contacts', [Contact], true)
    contacts: Contact[] = [];

    @JsonProperty('bank_details', [BankDetail], true)
    bankDetails: BankDetail[] = [];

    @JsonProperty('partners', [Partner], true)
    partners: Partner[] = [];

    @JsonProperty('notes', [Note], true)
    notes: Note[] = [];

    @JsonProperty('contract', ClientContract, true)
    contract: ClientContract = null;

    @JsonProperty('documents', [Document], true)
    documents: Document[] = [];

    @JsonProperty('reject_reason', String, true)
    rejectReason = null;

    @JsonProperty('reconnect_reason', String, true)
    reconnectReason = null;

    @JsonProperty('disconnect_reason', String, true)
    disconnectReason = null;

    @JsonProperty('is_approved', Boolean, true)
    isApproved = null;

    get address() {
        return this.streetAddress + ',\n' + this.location;
    }

    get statusStr() {
        return StatusEnum[this.status];
    }

    get primaryPhone() {
        return this.contacts.length > 0 ? this.contacts[0].phone : null;
    }

    get primaryEmail() {
        return this.contacts.length > 0 ? this.contacts[0].email : null;
    }

    get totalCases() {
        return this.openCasesCount + this.closedCasesCount;
    }

    get primaryBanks() {
        return this.bankDetails.filter(x => x.isPrimary);
    }
}

@JsonObject('ClientLite')
export class ClientLite extends ClientBase {
    @JsonProperty('relationship', Service, true)
    relationship: Service = null;

    @JsonProperty('services', [Service], true)
    services: Service[] = [];

    @JsonProperty('reject_reason', String, true)
    rejectReason = null;
}